import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type {
  AccessStatus,
  BulkDeactivateResult,
  AccountStats,
  FieldDefinition,
  Group,
  PeopleStats,
  PersonPage,
  PersonStatus,
  Site,
  SiteList,
} from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { formatDate, formatNumber, localized } from '../../shared/format';
import { ListError } from '../../shared/list-error';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  AdminPage,
  Avatar,
  Badge,
  Btn,
  Card,
  CardFoot,
  Checkbox,
  Empty,
  Input,
  PageHead,
  SearchField,
  Table,
  Toolbar,
  useAdminToast,
} from '../admin-kit';
import { AccessBadge } from './person-access-section';
import { AccountModal, NewPersonModal, TemporaryModal } from './person-dialogs';
import { PersonDrawer, type PersonTab } from './person-drawer';
import {
  BulkDeactivateModal,
  ExitListModal,
  SortableTh,
  type SortColumn,
  type SortDirection,
} from './people-bulk';
import { FilterMenu, RowMenu } from './people-ui';
import { PendingDeletionsNote } from './pending-deletions-note';
import { MemberInvitationsCard } from './member-invitations-card';

/**
 * De medewerkerslijst: het scherm waar HR het vaakst staat (sinds BHR-2 frame M1).
 *
 * Werksoftware, geen dashboard: een dichte tabel, filters die blijven staan, en een detailpaneel
 * (M2) dat over de lijst schuift zodat je niet telkens je plek kwijtraakt: zoekterm, filters en
 * geladen pagina's blijven erachter staan.
 */
export function PeoplePage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  // Wie alleen mag kijken, ziet de knop niet. De server weigert het endpoint sowieso;
  // dit voorkomt alleen dat iemand op een dichte deur klopt.
  const canManage = has('directory.person.manage');
  const adminToast = useAdminToast();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PersonStatus | ''>('');
  const [groupId, setGroupId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [field, setField] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  // Uit het globale zoeken: een medewerker aantikken opent zijn paneel, en niet alleen de lijst
  // waarin je hem opnieuw moet zoeken (testrapport 28 okt.).
  const { persoon } = useSearch({ strict: false }) as { persoon?: string };
  const navigate = useNavigate();
  const [selected, setSelected] = useState<{ id: string; tab: PersonTab } | null>(
    persoon ? { id: persoon, tab: 'details' } : null,
  );
  useEffect(() => {
    if (persoon) setSelected({ id: persoon, tab: 'details' });
  }, [persoon]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Stap 31: de filter "Tijdelijk" sorteert op einddatum en toont die als kolom.
  const [type, setType] = useState<'' | 'employee' | 'external'>('');
  const [temporaryOpen, setTemporaryOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const tijdelijk = type === 'external';
  // FBK-4: filteren op activatie, sorteren per kolom, en aanvinken om in bulk uit dienst te zetten.
  const [activation, setActivation] = useState<
    '' | 'active' | 'pending_activation' | 'disabled' | 'none'
  >('');
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection } | null>(null);
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exitListOpen, setExitListOpen] = useState(false);
  const sortColumn: SortColumn = sort?.column ?? (tijdelijk ? 'endDate' : 'name');
  const sortDirection: SortDirection = sort?.direction ?? 'asc';

  const groups = useQuery({
    queryKey: ['directory', 'groups'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/groups');
      return (data as { items: Group[] } | undefined)?.items ?? [];
    },
  });

  const fields = useQuery({
    queryKey: ['directory', 'fields'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/fields');
      return (data as { items: FieldDefinition[] } | undefined)?.items ?? [];
    },
  });

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async (): Promise<Site[]> => {
      const { data, error } = await api.GET('/api/v1/sites', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as SiteList).items;
    },
  });

  /*
    De zin onder de titel: "52 in dienst · 30 wachten op activatie". Dezelfde twee tellingen als
    de kaart Medewerkers op Start, onder dezelfde sleutels. Mislukt er een (een recht dat ontbreekt),
    dan valt dat stuk van de zin weg in plaats van het scherm.
  */
  const peopleStats = useQuery({
    queryKey: ['directory', 'stats'],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/stats');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PeopleStats;
    },
  });
  const accountStats = useQuery({
    queryKey: ['identity', 'stats'],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/accounts/stats');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as AccountStats;
    },
  });

  /*
    Een oneindige lijst in plaats van de eerste vijftig (punt 10).

    Er stond "Er zijn meer resultaten. Verfijn je zoekopdracht." onder de tabel - een zin die de
    gebruiker een opdracht geeft in plaats van hem de rest te tonen. De cursor was er al; alleen de
    knop ontbrak.
  */
  const people = useInfiniteQuery({
    queryKey: [
      'directory',
      'people',
      { search, status, groupId, siteId, field, fieldValue, type, activation, sort },
    ],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (laatste: PersonPage) => laatste.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = { limit: '50' };
      if (search.trim()) query.search = search.trim();
      if (status) query.status = status;
      if (groupId) query.groupId = groupId;
      if (siteId) query.siteId = siteId;
      if (field) query.field = field;
      if (field && fieldValue) query.fieldValue = fieldValue;
      if (type) query.type = type;
      if (tijdelijk) query.limit = '100';
      if (sortColumn !== 'name') query.sort = sortColumn;
      if (sortDirection === 'desc') query.direction = 'desc';
      if (pageParam) query.cursor = pageParam;

      // Activatie gaat over accounts: die lijst komt van identity, met dezelfde filters (FBK-4).
      const { data, error } = activation
        ? await api.GET('/api/v1/accounts/people', {
            params: { query: { ...query, activation } } as never,
          })
        : await api.GET('/api/v1/people', { params: { query } as never });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PersonPage;
    },
  });

  const personen = (people.data?.pages ?? []).flatMap((pagina) => pagina.items);

  /**
   * De activatiestatus van de zichtbare medewerkers, in één aanroep.
   *
   * Per rij vragen zou N+1 opleveren op precies het scherm waar HR met vijftig mensen
   * tegelijk kijkt; dit endpoint neemt er honderd ids in één keer. Na "meer laden" staan er
   * meer dan honderd, dus per honderd: één te lange aanroep is een 400 en dan staat elke rij
   * op "Geen account".
   */
  const personIds = personen.map((person) => person.id);
  const access = useQuery({
    queryKey: ['identity', 'access', personIds],
    enabled: personIds.length > 0,
    queryFn: async () => {
      const brokken: string[][] = [];
      for (let i = 0; i < personIds.length; i += 100) brokken.push(personIds.slice(i, i + 100));
      const delen = await Promise.all(
        brokken.map(async (brok) => {
          const { data, error } = await api.GET('/api/v1/accounts/status', {
            params: { query: { personIds: brok.join(',') } },
          });
          if (error || !data) throw error ?? new Error('Ophalen mislukt');
          return (data as { items: AccessStatus[] }).items;
        }),
      );
      return delen.flat();
    },
  });

  const accessByPerson = Object.fromEntries(
    (access.data ?? []).map((item) => [item.personId, item.status]),
  ) as Record<string, AccessStatus['status']>;

  const selectedField = fields.data?.find((definition) => definition.key === field);

  const sub = [
    peopleStats.data ? t('beheer.medewerkers.subActive', { count: peopleStats.data.active }) : null,
    accountStats.data && accountStats.data.pendingActivation > 0
      ? t('beheer.medewerkers.subPending', { count: accountStats.data.pendingActivation })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  /*
    Het totaal voor "52 resultaten" en "8 van 52 getoond". De lijst pagineert met een cursor en
    telt niet; zonder volgende pagina is het totaal wat er staat. Met een volgende pagina weten we
    het alleen zonder filters (of met alleen de status), uit dezelfde tellingen als de zin onder de
    titel. Anders staat er "50+ resultaten" en "50 getoond".
  */
  const meer = Boolean(people.hasNextPage);
  const aantal = personen.length;
  const zonderFilters = !search.trim() && !groupId && !siteId && !field && !type && !activation;
  const totaal = !meer
    ? aantal
    : zonderFilters && peopleStats.data
      ? status === 'active'
        ? peopleStats.data.active
        : status === 'deactivated'
          ? peopleStats.data.deactivated
          : peopleStats.data.active + peopleStats.data.deactivated
      : null;

  /** Na het uit dienst zetten: vensters dicht, vinkjes weg, en een zin met wat er gebeurde. */
  const afterBulk = (result: BulkDeactivateResult) => {
    setBulkOpen(false);
    setExitListOpen(false);
    setChecked(new Set());
    adminToast.show(
      result.skipped.length > 0
        ? t('peopleBulk.doneSkipped', { count: result.done, skipped: result.skipped.length })
        : t('peopleBulk.done', { count: result.done }),
    );
    void queryClient.invalidateQueries({ queryKey: ['directory'] });
    void queryClient.invalidateQueries({ queryKey: ['identity'] });
  };

  return (
    <AdminPage>
      <PageHead
        title={t('people.title')}
        sub={sub || undefined}
        actions={
          <>
            {has('directory.temporary.create') ? (
              <Btn
                variant="sec"
                icon={<AdminIcon name="clock" />}
                testId="people-new-temporary"
                onClick={() => setTemporaryOpen(true)}
              >
                {t('temporary.add')}
              </Btn>
            ) : null}
            {/* ACC-2: een account met naam, e-mail en rol, en de mail vertrekt meteen. */}
            {has('identity.invitation.create') ? (
              <Btn
                variant="sec"
                icon={<AdminIcon name="key" />}
                testId="people-create-account"
                onClick={() => setAccountOpen(true)}
              >
                {t('people.createAccount')}
              </Btn>
            ) : null}
            {canManage ? (
              <Btn
                variant="sec"
                icon={<AdminIcon name="upload" />}
                testId="people-exit-list-open"
                onClick={() => setExitListOpen(true)}
              >
                {t('peopleBulk.listOpen')}
              </Btn>
            ) : null}
            {canManage ? (
              <Btn icon={<AdminIcon name="plus" />} onClick={() => setDialogOpen(true)}>
                {t('people.new')}
              </Btn>
            ) : null}
          </>
        }
      />

      {/* DEL-1: wie gevraagd heeft om verwijderd te worden; alleen voor wie accounts beheert. */}
      <PendingDeletionsNote />

      {/* ADR-0101: leden die nog nooit toegang hadden (na de import), per pakje uitnodigen. */}
      <MemberInvitationsCard />

      <Card>
        <Toolbar
          count={
            people.isSuccess
              ? totaal === null
                ? t('beheer.medewerkers.resultsMore', { count: aantal })
                : t('beheer.medewerkers.results', { count: totaal })
              : undefined
          }
        >
          <SearchField label={t('people.search')} value={search} onChange={setSearch} />

          <FilterMenu
            label={t('people.status')}
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: t('people.allStatuses') },
              { value: 'active', label: t('people.active') },
              { value: 'deactivated', label: t('people.deactivated') },
            ]}
          />

          <FilterMenu
            label={t('access.columnTitle')}
            testId="people-activation-filter"
            value={activation}
            onChange={(value) => setActivation(value as typeof activation)}
            options={[
              { value: '', label: t('peopleBulk.activationAll') },
              { value: 'active', label: t('peopleBulk.activationActive') },
              { value: 'pending_activation', label: t('peopleBulk.activationPending') },
              { value: 'none', label: t('peopleBulk.activationNone') },
              { value: 'disabled', label: t('peopleBulk.activationDisabled') },
            ]}
          />

          <FilterMenu
            label={t('temporary.filter')}
            testId="people-type-filter"
            value={type}
            onChange={setType}
            options={[
              { value: '', label: t('temporary.filterAll') },
              { value: 'employee', label: t('temporary.filterEmployee') },
              { value: 'external', label: t('temporary.filterTemporary') },
            ]}
          />

          {/*
            Het filter op vestiging staat er alleen als er meer dan één is (Stap 29).

            Bij één vestiging is het een keuzelijst met één keuze: ruis op een scherm dat HR
            elke dag gebruikt. Het verschijnt vanzelf zodra er een tweede bijkomt.
          */}
          {(sites.data?.length ?? 0) > 1 ? (
            <FilterMenu
              label={t('sites.filter')}
              testId="people-site-filter"
              value={siteId}
              onChange={setSiteId}
              options={[
                { value: '', label: t('sites.all') },
                ...(sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
                { value: 'none', label: t('sites.none') },
              ]}
            />
          ) : null}

          <FilterMenu
            label={t('groups.title')}
            testId="people-group-filter"
            value={groupId}
            onChange={setGroupId}
            options={[
              { value: '', label: t('people.allGroups') },
              ...(groups.data ?? []).map((group) => ({ value: group.id, label: group.name })),
            ]}
          />

          <FilterMenu
            label={t('fields.title')}
            testId="people-field-filter"
            value={field}
            onChange={(waarde) => {
              setField(waarde);
              setFieldValue('');
            }}
            options={[
              { value: '', label: t('people.allFields') },
              ...(fields.data ?? []).map((definition) => ({
                value: definition.key,
                label: localized(definition.label) ?? definition.key,
              })),
            ]}
          />

          {field ? (
            selectedField?.type === 'select' ? (
              <FilterMenu
                label={t('people.fieldValue')}
                value={fieldValue}
                onChange={setFieldValue}
                options={[
                  { value: '', label: t('people.anyValue') },
                  ...(selectedField.options ?? []).map((option) => ({
                    value: option,
                    label: option,
                  })),
                ]}
              />
            ) : (
              <Input
                aria-label={t('people.fieldValue')}
                placeholder={t('people.fieldValue')}
                className="a-filter-input"
                /* Ook in de filter het juiste invoertype (punt 11): op een datumveld filteren
                   met een tekstvak levert "12/3" op, en daar vindt de server niets mee. */
                type={
                  selectedField?.type === 'date'
                    ? 'date'
                    : selectedField?.type === 'number'
                      ? 'number'
                      : 'text'
                }
                value={fieldValue}
                onChange={(event) => setFieldValue(event.target.value)}
              />
            )
          ) : null}
        </Toolbar>

        {people.isPending ? (
          <div className="a-card-pad">
            <LoadingState rows={6} label={t('common.loading')} />
          </div>
        ) : people.isError ? (
          <div className="a-card-pad">
            <ListError onRetry={() => void people.refetch()} error={people.error} />
          </div>
        ) : personen.length === 0 ? (
          <Empty
            icon={<AdminIcon name="people" size={24} />}
            title={t('people.empty')}
            text={t('beheer.medewerkers.emptyHint')}
          />
        ) : (
          <>
            <Table label={t('people.title')}>
              <thead>
                <tr>
                  {canManage ? (
                    <th className="sel">
                      <Checkbox
                        testId="people-check-all"
                        label={<span className="a-sr">{t('peopleBulk.checkAll')}</span>}
                        checked={
                          personen.length > 0 && personen.every((person) => checked.has(person.id))
                        }
                        onChange={(on) =>
                          setChecked(on ? new Set(personen.map((person) => person.id)) : new Set())
                        }
                      />
                    </th>
                  ) : null}
                  <SortableTh
                    label={t('people.name')}
                    column="name"
                    sort={sortColumn}
                    direction={sortDirection}
                    onSort={(column, direction) => setSort({ column, direction })}
                  />
                  <SortableTh
                    label={t('people.employeeNumber')}
                    column="employeeNumber"
                    sort={sortColumn}
                    direction={sortDirection}
                    onSort={(column, direction) => setSort({ column, direction })}
                  />
                  <SortableTh
                    label={t('people.language')}
                    column="language"
                    sort={sortColumn}
                    direction={sortDirection}
                    onSort={(column, direction) => setSort({ column, direction })}
                  />
                  <SortableTh
                    label={t('people.status')}
                    column="status"
                    sort={sortColumn}
                    direction={sortDirection}
                    onSort={(column, direction) => setSort({ column, direction })}
                  />
                  {tijdelijk ? (
                    <SortableTh
                      label={t('temporary.endDate')}
                      column="endDate"
                      sort={sortColumn}
                      direction={sortDirection}
                      onSort={(column, direction) => setSort({ column, direction })}
                    />
                  ) : null}
                  <th>{t('access.columnTitle')}</th>
                  <th className="act">
                    <span className="a-sr">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {personen.map((person) => {
                  const open = (tab: PersonTab) => setSelected({ id: person.id, tab });
                  return (
                    <tr key={person.id} className={person.id === selected?.id ? 'sel' : undefined}>
                      {canManage ? (
                        <td className="sel">
                          <Checkbox
                            testId="people-check"
                            label={
                              <span className="a-sr">
                                {person.firstName} {person.lastName}
                              </span>
                            }
                            checked={checked.has(person.id)}
                            onChange={(on) =>
                              setChecked((current) => {
                                const next = new Set(current);
                                if (on) next.add(person.id);
                                else next.delete(person.id);
                                return next;
                              })
                            }
                          />
                        </td>
                      ) : null}
                      <td>
                        <div className="a-person">
                          <Avatar name={`${person.firstName} ${person.lastName}`} />
                          <div style={{ minWidth: 0 }}>
                            <button
                              type="button"
                              className="a-name a-rowlink"
                              onClick={() => open('details')}
                            >
                              {person.lastName}, {person.firstName}
                            </button>
                            {person.personType !== 'employee' ? (
                              <div className="a-meta">
                                <Badge tone="navy">
                                  {person.personType === 'contractor'
                                    ? t('temporary.typeContractor')
                                    : t('temporary.typeTemporary')}
                                </Badge>
                                {person.originCompany ? ` ${person.originCompany}` : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        {person.employeeNumber ? (
                          <span className="a-mono">{person.employeeNumber}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{person.preferredLanguage.toUpperCase()}</td>
                      <td>
                        {person.status === 'active' ? t('people.active') : t('people.deactivated')}
                      </td>
                      {tijdelijk ? (
                        <td data-testid="people-end-date">
                          {person.plannedExitDate
                            ? formatDate(`${person.plannedExitDate}T12:00:00`)
                            : '—'}
                        </td>
                      ) : null}
                      <td>
                        <AccessBadge status={accessByPerson[person.id] ?? 'none'} />
                      </td>
                      <td className="act">
                        <RowMenu
                          label={t('beheer.medewerkers.moreActions')}
                          items={[
                            {
                              key: 'details',
                              label: t('people.tabDetails'),
                              onSelect: () => open('details'),
                            },
                            {
                              key: 'groups',
                              label: t('people.tabGroups'),
                              onSelect: () => open('groups'),
                            },
                            {
                              key: 'access',
                              label: t('access.title'),
                              onSelect: () => open('access'),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <CardFoot>
              {checked.size > 0 ? (
                <Btn
                  variant="danger"
                  size="sm"
                  testId="people-bulk-open"
                  onClick={() => setBulkOpen(true)}
                >
                  {t('peopleBulk.open', { count: checked.size })}
                </Btn>
              ) : null}
              <span>
                {totaal === null
                  ? t('beheer.medewerkers.shown', { count: aantal })
                  : t('beheer.medewerkers.shownOf', {
                      count: totaal,
                      shown: formatNumber(aantal),
                      total: formatNumber(totaal),
                    })}
              </span>
              {meer ? (
                <Btn
                  variant="sec"
                  size="sm"
                  icon={<AdminIcon name="chevronDown" />}
                  testId="people-more"
                  disabled={people.isFetchingNextPage}
                  aria-busy={people.isFetchingNextPage}
                  onClick={() => void people.fetchNextPage()}
                >
                  {t('people.loadMore')}
                </Btn>
              ) : null}
            </CardFoot>
          </>
        )}
      </Card>

      {selected ? (
        /*
          `key={selected.id}`: zonder deze sleutel hergebruikt React het paneel van de vorige
          medewerker, met zijn staat erin. Samen met ongecontroleerde velden gaf dat de fout van
          6 oktober: A openen, dan B, dan opnieuw A (uit de cache) - het paneel toonde nog de
          waarden van B, en een blur schreef die op A.
        */
        <PersonDrawer
          key={selected.id}
          personId={selected.id}
          initialTab={selected.tab}
          groups={groups.data ?? []}
          fields={fields.data ?? []}
          onClose={() => {
            setSelected(null);
            // Uit de URL, zodat dezelfde medewerker nog eens zoeken het paneel weer opent.
            if (persoon) void navigate({ to: '/admin/people', search: {}, replace: true });
          }}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['directory'] })}
        />
      ) : null}

      {bulkOpen ? (
        <BulkDeactivateModal
          personIds={[...checked]}
          onClose={() => setBulkOpen(false)}
          onDone={afterBulk}
        />
      ) : null}

      {exitListOpen ? (
        <ExitListModal onClose={() => setExitListOpen(false)} onDone={afterBulk} />
      ) : null}

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />

      <TemporaryModal
        open={temporaryOpen}
        onClose={() => {
          setTemporaryOpen(false);
          // Ook de tellingen en de toegangsstatus: een tijdelijke krijgt meteen een account.
          void queryClient.invalidateQueries({ queryKey: ['directory'] });
          void queryClient.invalidateQueries({ queryKey: ['identity'] });
        }}
        groups={(groups.data ?? []).map((group) => ({ id: group.id, name: group.name }))}
      />

      <NewPersonModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={async (person) => {
          // Meteen op Toegang: iemand toevoegen geeft nog geen toegang (M3), en daar staat de
          // volgende stap.
          setSelected({ id: person.id, tab: 'access' });
          await queryClient.invalidateQueries({ queryKey: ['directory'] });
        }}
      />
      {adminToast.element}
    </AdminPage>
  );
}
